import type { TranslationTree } from '../../types';

export const mlRul: TranslationTree = {
  machineLearning: {
    modelPrediction: {
      title: 'Standzeitprognose',
      subtitle:
        'Verschleiß eines Werkzeugs aus seiner Rückverfolgungshistorie geschätzt, oder Reststandzeit aus den Maschinensignalen vorhergesagt.',
      recent: {
        title: 'Letzte Vorhersagen',
        runsTitle: 'Letzte Ausführungen',
        when: 'Wann',
        tool: 'Werkzeug',
        model: 'Modell',
        value: 'Ergebnis',
        helpful: 'Hilfreiche Vorhersage',
        notHelpful: 'Falsche Vorhersage',
      },
      views: { wear: 'Werkzeugverschleiß', signals: 'Live-Signale' },
    },
  },
  mlRul: {
    context: {
      badge: 'Kontext',
      reset: 'Zurücksetzen',
      step1Label: 'Maschine',
      step2Label: 'Prozess',
      step3Label: 'Werkzeug',
      step1Placeholder: 'Maschine auswählen',
      step2Placeholder: 'Prozess auswählen',
      step3PlaceholderManual: 'Werkzeug-ID eingeben',
      step3FromProcess: 'Aus Prozess',
      step3ManualId: 'Manuelle ID',
      step3Mode: 'Werkzeugauswahl',
      step3ManualHelper:
        'Geben Sie die Werkzeugkennung ein, wie sie auf dem Werkzeugetikett aufgedruckt ist.',
      step3NoTools: 'Diesem Prozess sind noch keine Werkzeuge zugewiesen.',
      step2NoProcesses: 'Auf dieser Maschine laufen keine Prozesse.',
      runButton: 'Inferenz starten',
      runButtonLoading: 'Inferenz läuft…',
      cancelButton: 'Abbrechen',
      retryButton: 'Erneut versuchen',
      step4Label: 'Schnittbedingungen',
      step4BadgeDefaults: 'Standard',
      step4BadgeFromProcess: 'aus Prozess',
      step4BadgeCustom: 'benutzerdefiniert',
      step4ResetToDefaults: 'Auf Standardwerte zurücksetzen',
      step4PreFilled: 'vorausgefüllt',
      signalSourceLabel: 'Signalquelle',
      signalSourcePending: 'Wird angezeigt, sobald eine Maschine gewählt ist.',
      signalSourceMissing:
        'Diese Maschine hat keine Signalquelle, daher kann keine Vorhersage laufen. Verknüpfen Sie eine unter Werkstatt → Maschinen.',
      runNeedsSignalSource:
        'Verknüpfen Sie eine Signalquelle mit dieser Maschine, um die Vorhersage zu starten.',
      params: {
        fz: 'Vorschub je Zahn',
        ap: 'Axiale Schnitttiefe',
        ae: 'Radiale Schnitttiefe',
        vc: 'Schnittgeschwindigkeit',
        nbDents: 'Anzahl der Zähne',
        diameterCut: 'Werkzeugdurchmesser',
        units: {
          fz: 'mm/z',
          ap: 'mm',
          ae: 'mm',
          vc: 'm/min',
          nbDents: '—',
          diameterCut: 'mm',
        },
      },
    },
    tool: {
      unknownReference: 'Unbekannte Referenz',
      lastUsed: 'Zuletzt verwendet am {{date}}',
      lastUsedNever: 'Noch nie verwendet',
      statusInUse: 'Im Einsatz',
      statusIdle: 'Inaktiv',
      statusScrapped: 'Ausgemustert',
    },
    result: {
      badgeIdle: 'Wartet auf Inferenz',
      badgeRunning: 'Inferenz läuft',
      badgeCompleted: 'Inferenz abgeschlossen',
      badgeFailed: 'Inferenz fehlgeschlagen',
      idleTitle: 'Maschine, Prozess und Werkzeug auswählen',
      idleDescription:
        'Die verbleibende Nutzungsdauer wird hier angezeigt, sobald Sie eine Inferenz starten.',
      runningOnTool: 'Inferenz läuft für {{toolId}}',
      rulLabel: 'Verbleibende Nutzungsdauer',
      secondsUnit: 'Sekunden',
      humanReadable: 'ungefähr {{hours}} Stunden {{minutes}} Minuten verbleibende Schnittzeit',
      humanReadableSeconds: 'ungefähr {{seconds}} Sekunden verbleibende Schnittzeit',
      humanReadableDays: 'ungefähr {{days}} Tage {{hours}} Stunden verbleibende Schnittzeit',
      contextChipMachine: 'Maschine',
      contextChipProcess: 'Prozess',
      contextChipTool: 'Werkzeug',
      contextChipJob: 'Auftrag',
      errorTitle: 'Inferenz fehlgeschlagen',
      window: {
        part: 'Teil {{part}}',
        passes: { one: '{{count}} Durchgang', other: '{{count}} Durchgänge' },
        toolVolume: 'Werkzeugvolumen {{volume}} cm³',
      },
    },
    signal: {
      badge: 'Inferenzsignal',
      helper: 'Letzte {{passes}} Durchgänge · wie vom Modell gelesen',
      helperPart: 'Letzte {{passes}} Durchgänge von Teil {{part}} · wie vom Modell gelesen',
      openLink: 'In Signalvisualisierung öffnen →',
      errorTitle: 'Signal nicht verfügbar',
      errorRetry: 'Erneut versuchen',
    },
    errors: {
      missingSignalSource:
        'Diese Maschine hat keine Signalquelle. Verknüpfen Sie eine unter Werkstatt → Maschinen.',
      noSignalData:
        'Die Signaldatenbank enthält in den letzten 90 Tagen keine Daten für die Signalquelle dieser Maschine.',
      noMachiningPass:
        'Kein Bearbeitungsdurchgang, den das Modell kennt. Die letzten {{seconds}} s des Teils {{part}} enthalten keinen Durchgang des Laborblocks (|X|, |Y| ≤ 13 mm, Z ≤ 0). Das Live-Modell wurde nur mit diesen Durchgängen trainiert.',
      noMachiningPassGeneric:
        'Kein Bearbeitungsdurchgang, den das Modell kennt, im neuesten Teil der Maschine: kein Durchgang des Laborblocks (|X|, |Y| ≤ 13 mm, Z ≤ 0). Das Live-Modell wurde nur mit diesen Durchgängen trainiert.',
    },
  },
  mlWear: {
    context: {
      tool: 'Werkzeug',
      toolPlaceholder: 'Werkzeug wählen',
      toolTraces: {
        one: '{{count}} Spur · letzte am {{date}}',
        other: '{{count}} Spuren · letzte am {{date}}',
      },
      toolNoTrace: 'Keine Spur für dieses Werkzeug.',
      toolsLoadError: 'Die Werkzeugliste konnte nicht geladen werden.',
      diameter: 'Schneidendurchmesser',
      diameterField: 'Schneiden-Ø',
      required: 'erforderlich',
      diameterSource: 'Quelle des Durchmessers',
      modeAdaptcut: 'Adaptcut-Werkzeug',
      modeManual: 'Eingegeben',
      adaptcutPlaceholder: 'Adaptcut-Werkzeug wählen',
      adaptcutSearch: 'Name oder Referenz suchen',
      adaptcutList: 'Adaptcut-Werkzeuge',
      adaptcutNoMatch: 'Kein Adaptcut-Werkzeug passt.',
      adaptcutSameReference: 'gleiche Referenz',
      adaptcutTag: 'Adaptcut',
      adaptcutLine: 'Schneiden-Ø {{cut}} · {{teeth}} Zähne · Schaft-Ø {{shank}}',
      adaptcutNoDiameter:
        'Dieses Adaptcut-Werkzeug hat keinen Schneidendurchmesser. Geben Sie ihn unten ein.',
      adaptcutError: 'Die Liste der Adaptcut-Werkzeuge konnte nicht geladen werden.',
      retry: 'Erneut versuchen',
      diameterHelpUsed: 'Gilt für die ganze Historie und für den nächsten Arbeitsgang.',
      diameterHelpRequired: 'Geben Sie den Schneidendurchmesser des Werkzeugs ein.',
      diameterHelpNoMatch:
        'Kein Adaptcut-Werkzeug hat die Referenz {{reference}}: Geben Sie den Schneidendurchmesser ein oder wählen Sie ein anderes Adaptcut-Werkzeug.',
      diameterHelpAdaptcutDown:
        'Adaptcut ist nicht erreichbar: Geben Sie den Schneidendurchmesser ein.',
      diameterHelpPick: 'Wählen Sie ein Adaptcut-Werkzeug oder geben Sie den Durchmesser ein.',
      next: 'Nächster Arbeitsgang',
      optional: 'optional',
      fromTrace: 'letzte Spur',
      edited: 'geändert',
      volumePerPiece: 'Volumen pro Teil',
      plannedPieces: 'Geplante Teile',
      nextHelpFromTrace: 'Volumen pro Teil = Volumen ÷ Teile der letzten Spur.',
      nextHelpIgnored: 'ap und vc ändern die Vorhersage nicht.',
      backToTrace: 'Letzte Spur wieder verwenden',
      save: 'Vorhersage speichern',
      saving: 'Wird gespeichert…',
      saved: 'Vorhersage gespeichert',
      saveError: 'Die Vorhersage konnte nicht gespeichert werden.',
      needsDiameter: 'Geben Sie den Schneidendurchmesser ein, um den Verschleiß zu berechnen.',
      units: { mm: 'mm', mmPerTooth: 'mm/Zahn', cm3: 'cm³', pieces: 'Stk.' },
    },
    result: {
      idle: 'Wählen Sie ein Werkzeug, um seinen Verschleiß zu schätzen.',
      needsDiameter:
        'Geben Sie den Schneidendurchmesser (Schritt 1) ein, um den Verschleiß zu berechnen.',
      loading: 'Verschleiß wird berechnet…',
      modelUnavailableTitle: 'Das Verschleißmodell ist auf diesem Server nicht bereitgestellt.',
      modelUnavailable:
        'Ein Administrator muss wear_factor in der Modellregistry registrieren. Die Historie des Werkzeugs bleibt in der Rückverfolgung sichtbar.',
      error: 'Der Verschleiß konnte nicht berechnet werden.',
      reportedWorn: 'Werkzeug in seiner Spur vom {{date}} als verschlissen gemeldet.',
      reportedBroken: 'Werkzeug in seiner Spur vom {{date}} als gebrochen gemeldet.',
      reportedDetail:
        'Das Modell schätzt seinen Verschleiß auf {{wear}}, mit {{pWorn}} Wahrscheinlichkeit, verschlissen zu sein.',
      outOfRangeTitle:
        'Außerhalb des Anpassungsbereichs des Modells: Das Ergebnis ist eine Extrapolation.',
      outOfRange: {
        material: 'Werkstoff {{values}} (angepasst auf {{fitted}})',
        ap: 'ap {{values}} mm (angepasst auf {{fitted}} mm)',
        ae: 'ae {{values}} mm (angepasst auf {{fitted}} mm)',
        diameter: 'Durchmesser {{values}} mm (angepasst auf {{fitted}} mm)',
        hm: 'Spandicke hm {{values}} µm (angepasst auf {{fitted}} µm)',
      },
      stateTitle: 'Zustand des Werkzeugs',
      tracesCounted: {
        one: '{{count}} Spur gezählt',
        other: '{{count}} Spuren gezählt',
      },
      tracesSkipped: {
        one: '{{count}} ignoriert',
        other: '{{count}} ignoriert',
      },
      wear: 'Geschätzter Verschleiß',
      wearOfLife: 'der vorhergesagten Standzeit',
      pWorn: 'Wahrscheinlichkeit, bereits verschlissen zu sein',
      milled: 'Bereits gefräst',
      milledPieces: {
        one: '{{count}} Teil',
        other: '{{count}} Teile',
      },
      gaugeLabel: 'Verschleiß {{wear}} der vorhergesagten Standzeit',
      gaugePrudent: 'vorsichtig {{value}}',
      gaugeEnd: 'vorhergesagtes Standzeitende',
      capacityTitle: 'Noch fräsbar',
      capacitySettings: 'fz {{fz}} · ae {{ae}} · Ø {{d}} → hm {{hm}} µm',
      outOfRangeTag: 'außerhalb des Bereichs',
      overestimatedTag: 'überschätzt',
      lowerBoundTag: 'Minimum',
      missingCell: 'fehlt',
      incomplete: {
        titleSome: {
          one: 'Verschleiß unterschätzt: {{count}} von {{total}} Spuren wird nicht gezählt',
          other: 'Verschleiß unterschätzt: {{count}} von {{total}} Spuren werden nicht gezählt',
        },
        titleNone: {
          one: 'Keine verwendbare Spur: Die einzige Spur wird nicht gezählt',
          other: 'Keine verwendbare Spur: Keine der {{count}} Spuren wird gezählt',
        },
        consequence:
          'Verschleiß und Wahrscheinlichkeit sind Mindestwerte; was das Werkzeug noch fräsen kann, ist überschätzt.',
        missing: { one: '{{fields}} fehlt', other: '{{fields}} fehlen' },
        link: 'Spuren auf der Werkzeugseite ergänzen →',
      },
      capacityEmpty:
        'Geben Sie fz und ae des nächsten Arbeitsgangs ein, um zu schätzen, was das Werkzeug noch fräsen kann.',
      prudent: 'Vorsichtig',
      prudentHelp: '1 von 10 Werkzeugen wäre vorher verschlissen.',
      median: 'Median',
      medianHelp: 'Die Hälfte der Werkzeuge wäre vorher verschlissen.',
      piecesUnit: 'Teile',
      needsVolumePerPiece: 'Geben Sie das Volumen pro Teil ein, um die Teile zu zählen.',
      planned:
        'Nach den {{pieces}} geplanten Teilen ({{volume}} cm³): Verschleiß {{wear}}, Wahrscheinlichkeit, verschlissen zu sein {{pWorn}}.',
      plannedRisky: 'Risiko über 1 zu 10.',
      plannedSafe: 'Innerhalb der vorsichtigen Marge.',
      historyTitle: 'Historie des Werkzeugs',
      historyHelp:
        'Jede Spur addiert ihr Volumen geteilt durch die Standzeit bei ihren Einstellungen.',
      historyEmpty: 'Keine Spur: Das Werkzeug gilt als neu.',
      columns: {
        date: 'Datum',
        operation: 'Arbeitsgang',
        machine: 'Maschine',
        material: 'Werkstoff',
        ap: 'ap',
        ae: 'ae',
        fz: 'fz',
        hm: 'hm µm',
        pieces: 'Teile',
        volume: 'Volumen cm³',
        wearAdded: 'Verschleiß +',
        cumulative: 'Kumuliert',
        state: 'Zustand',
      },
      skipped: 'nicht gezählt: {{reason}}',
      skippedReasons: { missing_volume: 'Volumen fehlt', missing_settings: 'ae oder fz fehlt' },
      provenance: 'Modell {{name}} {{version}} aus der Registry',
      provenanceFitted: 'angepasst auf {{tools}} Werkzeuge in {{materials}}, Ø {{diameter}} mm',
      provenanceError: 'Standzeitfehler {{error}} min in der Kreuzvalidierung',
    },
    recent: {
      left: 'Noch fräsbar (Median)',
      settings: 'Einstellungen',
      leftPieces: '{{pieces}} Teile · {{volume}} cm³',
      leftVolume: '{{volume}} cm³',
    },
  },
};
